import { type CSSProperties, FC, useEffect, useRef } from "react";

const WIDGET_SCRIPT_SRC =
  "https://assets.calendly.com/assets/external/widget.js";

type CalendlyWindow = Window & {
  Calendly?: {
    initInlineWidget: (opts: {
      url: string;
      parentElement: HTMLElement;
    }) => void;
  };
};

const calWin = () => window as CalendlyWindow;

function loadCalendlyScript(): Promise<void> {
  if (calWin().Calendly?.initInlineWidget) return Promise.resolve();

  const existing = document.querySelector(
    `script[src="${WIDGET_SCRIPT_SRC}"]`,
  ) as HTMLScriptElement | null;

  if (existing) {
    return new Promise((resolve) => {
      if (calWin().Calendly?.initInlineWidget) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = WIDGET_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Calendly script failed to load"));
    document.body.appendChild(script);
  });
}

/**
 * Calendly’s widget.js only auto-scans the DOM on first load. After React Router
 * client-side navigation, a new mount needs {@link initInlineWidget} explicitly.
 */
export const CalendlyInlineEmbed: FC<{
  url: string;
  className?: string;
  style?: CSSProperties;
}> = ({ url, className, style }) => {
  const parentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const parent = parentRef.current;
    if (!parent) return;

    parent.innerHTML = "";
    let cancelled = false;

    const run = async () => {
      try {
        await loadCalendlyScript();
        if (cancelled || !parentRef.current) return;
        calWin().Calendly?.initInlineWidget({
          url,
          parentElement: parentRef.current,
        });
      } catch (e) {
        console.error("Calendly embed failed", e);
      }
    };

    void run();

    return () => {
      cancelled = true;
      parent.innerHTML = "";
    };
  }, [url]);

  return (
    <div
      ref={parentRef}
      className={className}
      style={style}
      aria-label="Calendly scheduling"
    />
  );
};
